import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Droplets, Save } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PartialAreaConfig, SprayEquipmentPicker, WhpHarvestCheck } from '../../components/spray';
import sprayService from '../../services/spray';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { SprayApplication, RowSpacingPattern, WhpAssessment, ApplicationMethod } from '../../types/spray';
import { APPLICATION_METHOD_LABELS } from '../../types/spray';
import type { Equipment } from '../../types/equipment';

function SprayEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [spray, setSpray] = useState<SprayApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [title, setTitle]                 = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd]   = useState('');
  const [equipmentId, setEquipmentId]     = useState<number | null>(null);
  const [sprayerObj, setSprayerObj]       = useState<Equipment | null>(null);
  const [primeMoverId, setPrimeMoverId]   = useState<number | null>(null);
  const [primeMoverObj, setPrimeMoverObj] = useState<Equipment | null>(null);
  const [nozzleType, setNozzleType]       = useState('');
  const [ratePerHa, setRatePerHa]         = useState<number | ''>('');
  const [rowPattern, setRowPattern]       = useState<RowSpacingPattern>('every_row');
  const [coveragePct, setCoveragePct]     = useState(100);
  const [concentrationFactor, setConcentrationFactor] = useState(1.0);
  const [preNotes, setPreNotes]           = useState('');
  const [applicationMethod, setApplicationMethod] = useState<ApplicationMethod>('foliar_runoff');
  const [runoffPerPlant, setRunoffPerPlant] = useState<number | ''>('');
  const [inventoryLocationId, setInventoryLocationId] = useState<number | ''>('');
  const [inventoryLocations, setInventoryLocations] = useState<any[]>([]);

  // Moving the date moves the withholding period with it, so the check has to
  // run again here. A date change that creates a conflict re-holds the spray
  // for a Spray Admin — asking for the reason now is what stops it landing in
  // their queue with nothing to read.
  const [whp, setWhp] = useState<WhpAssessment | null>(null);
  const [whpChecking, setWhpChecking] = useState(false);
  const [whpReason, setWhpReason] = useState('');
  const { hasAccess: isSprayAdmin } = useModuleLicense('spray', 'admin');

  useEffect(() => {
    sprayService.getInventoryLocations().then(res => {
      if (res.success && res.data) setInventoryLocations(res.data as any[]);
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    sprayService.getApplication(Number(id)).then(res => {
      if (res.success && res.data) {
        const s = res.data;
        setSpray(s);
        setTitle(s.title);
        setScheduledDate(s.scheduled_date);
        setScheduledStart(s.scheduled_start_time || '');
        setScheduledEnd(s.scheduled_end_time || '');
        setEquipmentId(s.equipment_id ?? null);
        setPrimeMoverId(s.prime_mover_equipment_id ?? null);
        setNozzleType(s.nozzle_type || '');
        setRatePerHa(s.rate_per_ha != null ? Number(s.rate_per_ha) : '');
        setRowPattern(s.row_spacing_pattern);
        setCoveragePct(Number(s.spray_coverage_pct));
        setConcentrationFactor(Number(s.concentration_factor));
        setPreNotes(s.pre_spray_notes || '');
        setApplicationMethod(s.application_method ?? 'foliar_runoff');
        setRunoffPerPlant(s.runoff_l_per_plant != null ? Number(s.runoff_l_per_plant) : '');
        setInventoryLocationId(s.inventory_location_id ?? '');
      }
      setLoading(false);
    });
  }, [id]);

  // Re-check whenever the date moves away from the one already saved.
  useEffect(() => {
    // Nursery stock has no harvest date, so there is nothing to check.
    if (!spray || spray.is_nursery || !scheduledDate || scheduledDate === spray.scheduled_date) {
      setWhp(null);
      return;
    }
    let cancelled = false;
    setWhpChecking(true);
    sprayService.checkWhp({
      targets: (spray.targets || []).map(t => ({
        target_type: t.target_type,
        target_id: t.target_id,
      })),
      scheduled_date: scheduledDate,
      chemicals: (spray.chemicals || []).map(c => ({
        chemical_id: c.chemical_id,
        crop_registration_id: c.crop_registration_id ?? null,
      })),
      chemical_id: spray.chemical_id,
      crop_registration_id: spray.crop_registration_id ?? null,
    }).then(res => {
      if (!cancelled && res.success && res.data) setWhp(res.data);
    }).catch(() => {}).finally(() => {
      if (!cancelled) setWhpChecking(false);
    });
    return () => { cancelled = true; };
  }, [spray, scheduledDate]);

  const hasWhpConflicts = (whp?.conflicts.length ?? 0) > 0;

  const handleSave = async () => {
    if (!spray || !title || !scheduledDate) {
      setError('Title and scheduled date are required');
      return;
    }
    if (hasWhpConflicts && !whpReason.trim()) {
      setError('Record why this date is being chosen inside a withholding period.');
      return;
    }
    setSaving(true);
    setError(null);
    if (sprayerObj?.is_implement && (!primeMoverObj || !primeMoverObj.can_tow_implements)) {
      setError('Towed sprayers require a tractor that can tow implements');
      setSaving(false);
      return;
    }
    const res = await sprayService.updateApplication(spray.id, {
      title,
      scheduled_date: scheduledDate,
      scheduled_start_time: scheduledStart || undefined,
      scheduled_end_time: scheduledEnd || undefined,
      equipment_id: equipmentId,
      prime_mover_equipment_id: primeMoverId,
      nozzle_type: nozzleType || undefined,
      rate_per_ha: ratePerHa !== '' ? Number(ratePerHa) : null,
      row_spacing_pattern: rowPattern,
      spray_coverage_pct: coveragePct,
      concentration_factor: concentrationFactor,
      ...(spray.is_nursery ? {
        application_method: applicationMethod,
        runoff_l_per_plant: runoffPerPlant === '' ? null : Number(runoffPerPlant),
      } : {}),
      pre_spray_notes: preNotes || undefined,
      inventory_location_id: inventoryLocationId !== '' ? Number(inventoryLocationId) : null,
      whp_override_reason: hasWhpConflicts ? whpReason.trim() : undefined,
    });
    if (res.success) {
      navigate(`/spray/${spray.id}`);
    } else {
      setError((res as any).error?.message || 'Failed to save changes');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-neutral-100 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (!spray) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 text-center">
        <p className="text-neutral-500">Spray not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/spray')}>Back</Button>
      </div>
    );
  }

  if (spray.status !== 'created' && spray.status !== 'assigned') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 text-center">
        <p className="text-neutral-500">This spray cannot be edited in its current state.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(`/spray/${spray.id}`)}>
          View Spray
        </Button>
      </div>
    );
  }

  const totalAreaHa = spray.total_target_area_ha ? Number(spray.total_target_area_ha) : 0;

  return (
    <>
      <PageHeader
        title="Edit Spray"
        subtitle={spray.reference_number || spray.title}
        icon={<Droplets className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Spray title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Chemical info (read-only) */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
            <p className="text-xs text-neutral-400 mb-1 uppercase font-semibold tracking-wide">Chemical (cannot change)</p>
            <p className="text-sm font-medium text-neutral-800">{spray.chemical_name}</p>
            {spray.chemical_trade_name && (
              <p className="text-sm text-neutral-500">{spray.chemical_trade_name}</p>
            )}
          </div>

          {/* Rate */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Application rate</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={ratePerHa}
                onChange={e => setRatePerHa(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="w-32 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-500">{spray.rate_unit || 'L/ha'}</span>
            </div>
          </div>

          {/* A nursery spray has no row pattern or coverage: it is mixed per
              100 L and its area comes back from the GPS. */}
          {spray.is_nursery ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="edit-application-method" className="block text-sm font-medium text-neutral-700 mb-1">
                  How it goes on
                </label>
                <select
                  id="edit-application-method"
                  value={applicationMethod}
                  onChange={e => setApplicationMethod(e.target.value as ApplicationMethod)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                >
                  {(Object.keys(APPLICATION_METHOD_LABELS) as ApplicationMethod[]).map(m => (
                    <option key={m} value={m}>{APPLICATION_METHOD_LABELS[m]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="edit-runoff" className="block text-sm font-medium text-neutral-700 mb-1">
                  Run-off per plant (optional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="edit-runoff"
                    type="number"
                    min="0"
                    step="0.01"
                    value={runoffPerPlant}
                    onChange={e => setRunoffPerPlant(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    className="w-28 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <span className="text-sm text-neutral-500">L per plant</span>
                </div>
              </div>
            </div>
          ) : (
          <div>
            <p className="text-sm font-medium text-neutral-700 mb-3">Coverage & concentration</p>
            <PartialAreaConfig
              rowPattern={rowPattern}
              coveragePct={coveragePct}
              concentrationFactor={concentrationFactor}
              ratePerHa={ratePerHa !== '' ? Number(ratePerHa) : null}
              rateUnit={spray.rate_unit}
              totalTargetAreaHa={totalAreaHa}
              whpDays={spray.whp_days}
              scheduledDate={scheduledDate}
              onRowPatternChange={setRowPattern}
              onCoverageChange={setCoveragePct}
              onConcentrationChange={setConcentrationFactor}
            />
          </div>
          )}

          {/* Schedule */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Scheduled date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={e => setScheduledDate(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:col-span-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start time</label>
                <input
                  type="time"
                  value={scheduledStart}
                  onChange={e => setScheduledStart(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End time</label>
                <input
                  type="time"
                  value={scheduledEnd}
                  onChange={e => setScheduledEnd(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          <SprayEquipmentPicker
            sprayerId={equipmentId}
            primeMoverId={primeMoverId}
            onSprayerChange={(id, eq) => {
              setEquipmentId(id);
              setSprayerObj(eq);
            }}
            onPrimeMoverChange={(id, eq) => {
              setPrimeMoverId(id);
              setPrimeMoverObj(eq);
            }}
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Nozzle type</label>
            <input
              type="text"
              value={nozzleType}
              onChange={e => setNozzleType(e.target.value)}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Inventory location</label>
            <select
              value={inventoryLocationId}
              onChange={e => setInventoryLocationId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Not specified</option>
              {inventoryLocations.map((loc: any) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Pre-spray notes</label>
            <textarea
              value={preNotes}
              onChange={e => setPreNotes(e.target.value)}
              rows={3}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          {/* Withholding period against the target blocks' harvest dates, for the
              date currently in the form. */}
          {!spray.is_nursery && (
            <WhpHarvestCheck
              assessment={whp}
              loading={whpChecking}
              reason={whpReason}
              onReasonChange={setWhpReason}
              viewerIsSprayAdmin={isSprayAdmin}
            />
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => navigate(`/spray/${spray?.id}`)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || whpChecking || (hasWhpConflicts && !whpReason.trim())}
            >
              <Save className="h-4 w-4" />
              {saving
                ? 'Saving...'
                : hasWhpConflicts ? 'Save & refer to Spray Admin' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default SprayEdit;
